/**
 * Booth OS theme registry — booth-only for v1 per Craig's lock.
 *
 * These themes do NOT cascade into gallery / wall / archive / recap.
 * They affect ONLY the booth kiosk surface:
 *   • launcher card backgrounds + accent gold
 *   • polaroid frame tones + tape colour
 *   • QR continuity background
 *
 * Each theme inherits the editorial constitution — warm walnut bases,
 * parchment text, gold accents — but shifts the temperature so a
 * Botanical Wedding doesn't feel the same as a Midnight Gold night.
 *
 * v2 will let event owners pre-select a default theme for their
 * booth via the host setup tab. For now the operator picks at
 * mount time and it persists in sessionStorage for the night.
 */

export const BOOTH_THEMES = {
  rustic_evening: {
    id: 'rustic_evening',
    label: 'Rustic Evening',
    eyebrow: 'WARM WALNUT · CANDLELIGHT',
    description: 'Walnut, parchment, and a low gold flame — the standard editorial atmosphere.',
    bg: '#1c1108',
    bgHi: '#2a190a',
    paper: 'rgba(20,12,6,0.72)',
    ink: '#FFE7A8',
    inkMuted: 'rgba(255,235,210,0.62)',
    inkFaint: 'rgba(255,235,210,0.38)',
    gold: '#D4AF5E',
    goldDeep: '#B8923F',
    hair: 'rgba(212,170,82,0.32)',
    polaroidFrame: '#F5EAD2',
    polaroidTape: '#C9A05C',
  },
  botanical_wedding: {
    id: 'botanical_wedding',
    label: 'Botanical Wedding',
    eyebrow: 'SAGE · IVORY · LIVING ROOM',
    description: 'Soft sage, candle-cream, and a quieter gold — for garden ceremonies and tented receptions.',
    bg: '#15211A',
    bgHi: '#1F2D24',
    paper: 'rgba(20,28,22,0.74)',
    ink: '#F2E9D2',
    inkMuted: 'rgba(242,233,210,0.62)',
    inkFaint: 'rgba(242,233,210,0.38)',
    gold: '#C7B17A',
    goldDeep: '#A8945F',
    hair: 'rgba(199,177,122,0.30)',
    polaroidFrame: '#F2E9D2',
    polaroidTape: '#A8B59A',
  },
  glam_celebration: {
    id: 'glam_celebration',
    label: 'Glam Celebration',
    eyebrow: 'NOIR · BRASS · LATE HOUR',
    description: 'Black-velvet, deep brass, and a soft sparkle in the corners — for the high-celebration nights.',
    bg: '#0E0A0D',
    bgHi: '#1B1216',
    paper: 'rgba(20,12,16,0.78)',
    ink: '#FBE7B9',
    inkMuted: 'rgba(251,231,185,0.62)',
    inkFaint: 'rgba(251,231,185,0.38)',
    gold: '#E0BE6C',
    goldDeep: '#B89342',
    hair: 'rgba(224,190,108,0.34)',
    polaroidFrame: '#F9EBC5',
    polaroidTape: '#D5A560',
  },
  elegant_ivory: {
    id: 'elegant_ivory',
    label: 'Elegant Ivory',
    eyebrow: 'IVORY · PARCHMENT · DAYLIGHT',
    description: 'A softer, paler atmosphere — daytime ceremonies, afternoon weddings, christenings.',
    bg: '#1F1A14',
    bgHi: '#2A2419',
    paper: 'rgba(28,24,18,0.74)',
    ink: '#F5E8C8',
    inkMuted: 'rgba(245,232,200,0.65)',
    inkFaint: 'rgba(245,232,200,0.40)',
    gold: '#CDB37A',
    goldDeep: '#A8915C',
    hair: 'rgba(205,179,122,0.32)',
    polaroidFrame: '#FAF3DE',
    polaroidTape: '#D7C28E',
  },
  midnight_gold: {
    id: 'midnight_gold',
    label: 'Midnight Gold',
    eyebrow: 'BLUE-BLACK · DEEP GOLD · LATE NIGHT',
    description: 'For after-midnight nights — the deepest atmosphere, the warmest gold against blue-black.',
    bg: '#0A0E1A',
    bgHi: '#121728',
    paper: 'rgba(12,16,28,0.78)',
    ink: '#F8E5A8',
    inkMuted: 'rgba(248,229,168,0.62)',
    inkFaint: 'rgba(248,229,168,0.38)',
    gold: '#E3C474',
    goldDeep: '#B89844',
    hair: 'rgba(227,196,116,0.32)',
    polaroidFrame: '#F4E7C0',
    polaroidTape: '#D9B26C',
  },
};

export const DEFAULT_THEME_ID = 'rustic_evening';

export const getTheme = (id) => BOOTH_THEMES[id] || BOOTH_THEMES[DEFAULT_THEME_ID];
